import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, input } from "@angular/core";
import { BaseListComponent, EvGridTableModule, ITableHeader, ITableLayoutModel } from "ev-grid-table";
import { EVNDAreaSubGroup } from "src/models/new-dwelling/new-dwelling.enums";
import { IUnitsFilterGroupItem } from "src/app/infopage/new-dwelling/newdwelling.model";

@Component({
    selector: "rooms-groups",
    imports: [CommonModule, EvGridTableModule],
    templateUrl: "./rooms-groups.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class RoomsGroupsComponent extends BaseListComponent<IUnitsFilterGroupItem> {
    rooms = input<IUnitsFilterGroupItem[]>([]);

    override originalData = computed(() =>
        (this.rooms() ?? []).map(room => {
            room.subValueDisplay = this.mapSubValueDisplay(room.groupKeyValue, room.subValue);
            return { ...room, subValueDisplay: this.mapSubValueDisplay(room.groupKeyValue, room.subValue) };
        }),
    );

    override tableData = computed(() => {
        const { sortOption } = this.sortConfig();
        const data = [...(this.originalData() ?? [])];
        if (data.length === 0) {
            return [];
        }

        const d = sortOption.desc ? 1 : -1;
        data.sort((a, b) => {
            const av = this.sortValue(a, sortOption.prop);
            const bv = this.sortValue(b, sortOption.prop);
            return av === bv ? 0 : av > bv ? -1 * d : 1 * d;
        });

        return data.slice(0, this.numberOfRowsConfig().rowsToShow);
    });

    private sortValue(row: IUnitsFilterGroupItem, prop: string): number {
        switch (prop) {
            case "total":
                return row.total?.numberOfUnits ?? 0;
            case "sold":
                return row.sold?.numberOfUnits ?? 0;
            case "unsold":
                return row.unsold?.numberOfUnits ?? 0;
            default:
                return row.groupKeyValue;
        }
    }

    protected override createHeaders(): ITableHeader[] {
        return [
            { name: "Rom", prop: "groupKeyValue", desc: false, type: "number", isSortable: true },
            { name: "Tot", prop: "total", desc: true, type: "number", isSortable: true, textAlignment: "right" },
            { name: "Solgt", prop: "sold", desc: true, type: "number", isSortable: true, textAlignment: "right" },
            { name: "Usolgt", prop: "unsold", desc: true, type: "number", isSortable: true, textAlignment: "right" },
        ];
    }

    protected override createDisplayTemplate(): ITableLayoutModel {
        return {
            breakPoints: [
                {
                    gridAreas: '"groupKeyValue total sold unsold"',
                    gridColumns: "1fr 40px 50px 50px",
                },
            ],
        };
    }

    private mapSubValueDisplay(groupKeyValue: number, subValue: EVNDAreaSubGroup): { min: string; max: string } | undefined {
        switch (subValue) {
            case EVNDAreaSubGroup.Large:
                return this.numRoomSizeLookup[`${groupKeyValue}-${EVNDAreaSubGroup.Large}` as keyof typeof this.numRoomSizeLookup];
            case EVNDAreaSubGroup.Medium:
                return this.numRoomSizeLookup[`${groupKeyValue}-${EVNDAreaSubGroup.Medium}` as keyof typeof this.numRoomSizeLookup];
            case EVNDAreaSubGroup.Small:
                return this.numRoomSizeLookup[`${groupKeyValue}-${EVNDAreaSubGroup.Small}` as keyof typeof this.numRoomSizeLookup];
            default:
                return undefined;
        }
    }

    private numRoomSizeLookup = {
        '2-small': { min: '', max: '40 m²' },
        '2-medium': { min: '40 m²', max: '50 m²' },
        '2-large': { min: '50 m²', max: '' },
        '3-small': { min: '', max: '60 m²' },
        '3-medium': { min: '60m²', max: '70m²' },
        '3-large': { min: '70m²', max: '' },
        '4-small': { min: '', max: '85m²' },
        '4-medium': { min: '85 m²', max: '100 m²' },
        '4-large': { min: '100 m²', max: '' },
    };
}
